import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ArrowSquareOutIcon, FilePdfIcon } from '@phosphor-icons/react';

import { citationAnchorId } from '../utils';

import { ReferenceItem } from './References';

/** A reference card, plus the citation number an inline `[n]` marker points at. */
type ReferenceProps = ReferenceItem & { number?: number };

export const Reference: React.FC<ReferenceProps> = observer(({ name, url, number }) => {
  const theme = useTheme();

  const handleClick = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Button
      // Anchor for an inline `[n]` chip to scroll to.
      id={number === undefined ? undefined : citationAnchorId(number)}
      onClick={handleClick}
      // A source with no URL still earns a card so every `[n]` resolves to
      // something visible — it just isn't a link.
      disabled={!url}
      variant="outlined"
      sx={{
        width: 172,
        minWidth: 172,
        height: 76,
        backgroundColor: '#F5F5F5',
        color: 'text.primary',
        borderColor: 'divider',
        borderRadius: 2,
        textTransform: 'none',
        textAlign: 'left',
        p: 1.25,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        '&:hover': {
          backgroundColor: '#EEEEEE',
          borderColor: 'divider',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
          {number !== undefined && (
            // The number an inline citation badge in the answer refers to. Plain
            // text, not a coloured pill — the card is already a bordered surface
            // and a second accent colour on it only competes with the title.
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1.25,
                marginTop: '1px',
              }}
            >
              {number}.
            </Box>
          )}
          <FilePdfIcon color={theme.palette.text.primary} size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 400,
              fontSize: '0.75rem',
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {name}
          </Typography>
        </Box>
        <ArrowSquareOutIcon size={16} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
      </Box>
    </Button>
  );
});
